import React, { ReactElement } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography,
} from '@mui/material';
import { dealStageOptions, DealStageType } from '../../../types/deals/deals';
import { formatDateTimeToLocal } from '../../../formatters/dateFormatters/dateFormatters';
import { useViewDealStyles } from '../../../styles/useViewDealStyles';
import { useViewLeaseStyles } from '../../../styles/useViewLeaseStyles';
import { useGetDealStageHistoryQuery } from '../../../queries/deals/stageHistory';
import ComponentLoader from '../../ui/ComponentLoader';

interface DealHistoryTimelineProps {
  dealId: number;
  currentStage?: string;
}

const DealHistoryTimeline: React.FC<DealHistoryTimelineProps> = ({ dealId, currentStage }): ReactElement => {
  const dealClasses = useViewDealStyles();
  const leaseClasses = useViewLeaseStyles();
  const dealStage = new DealStageType(dealStageOptions);

  // Use dedicated API query instead of deal.history
  const { data: stageHistory, isLoading, error } = useGetDealStageHistoryQuery(dealId);

  // Helper function to check if this is the current stage
  const isCurrentStage = (stage: string) => currentStage === stage;

  if (isLoading) {
    return <ComponentLoader isOpen />;
  }

  if (error) {
    return (
      <Box className={dealClasses.abstractRoundBorderedCard}>
        <Typography variant="overline" display="block" className={leaseClasses.abstractTitles}>
          DEAL STAGES TIMELINE
        </Typography>
        <Box className={dealClasses.noCommentsContainer}>
          <Typography variant="inherit" color="error">
            Failed to load stage history
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!stageHistory || stageHistory.length === 0) {
    return (
      <Box className={dealClasses.abstractRoundBorderedCard}>
        <Typography variant="overline" display="block" className={leaseClasses.abstractTitles}>
          DEAL STAGES TIMELINEY
        </Typography>
        <Box className={dealClasses.noCommentsContainer}>
          <Typography variant="inherit">No stage changes recorded yet</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={dealClasses.abstractRoundBorderedCard}>
      <Typography variant="overline" display="block" className={leaseClasses.abstractTitles}>
        DEAL STAGES TIMELINE
      </Typography>
      <Box style={{
        padding: '16px 0',
        overflowX: 'auto',
        overflowY: 'hidden',
        position: 'relative',
      }}
      >
        {/* Continuous horizontal line */}
        <Box style={{
          position: 'absolute',
          top: '24px',
          left: '10%',
          right: '10%',
          height: '2px',
          backgroundColor: '#e0e0e0',
          zIndex: 0,
        }}
        />

        {/* Timeline items container - each item contains both circle and label */}
        <Box style={{
          display: 'flex',
          flexDirection: 'row-reverse',
          width: '100%',
          position: 'relative',
          zIndex: 1,
        }}
        >
          {stageHistory.map((historyItem) => {
            const isCurrent = isCurrentStage(historyItem.stage);
            return (
              <Box
                key={historyItem.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                }}
              >
                {/* Circle */}
                <Box
                  style={{
                    width: isCurrent ? '20px' : '16px',
                    height: isCurrent ? '20px' : '16px',
                    borderRadius: '50%',
                    backgroundColor: dealStage.getColorByKey(historyItem.stage),
                    border: isCurrent ? '3px solid #4051B5' : 'none',
                    boxShadow: isCurrent ? '0 0 0 2px rgba(64, 81, 181, 0.2)' : 'none',
                    transition: 'all 0.2s ease-in-out',
                    zIndex: 2,
                    marginBottom: '16px',
                  }}
                />

                {/* Label content */}
                <Box style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  minHeight: '80px',
                  width: '100%',
                  paddingLeft: '4px',
                  paddingRight: '4px',
                }}
                >
                  <Typography
                    variant="h6"
                    component="div"
                    style={{
                      fontWeight: isCurrent ? 'bolder' : 'bold',
                      marginBottom: '8px',
                      fontSize: isCurrent ? '15px' : '14px',
                      lineHeight: '1.2',
                      color: isCurrent ? '#4051B5' : 'inherit',
                    }}
                  >
                    {dealStage.getDisplayNameByKey(historyItem.stage)}
                    {isCurrent && (
                      <Typography
                        component="span"
                        style={{
                          display: 'inline',
                          fontSize: '10px',
                          color: '#4051B5',
                          fontWeight: 'normal',
                          marginLeft: '6px',
                          verticalAlign: 'middle',
                        }}
                      >
                        (Current)
                      </Typography>
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{
                      color: '#666',
                      display: 'block',
                      marginBottom: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {formatDateTimeToLocal(historyItem.stageChangeDate)}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: '#666',
                      fontSize: '11px',
                      lineHeight: '1.3',
                    }}
                  >
                    Changed by:
                    {' '}
                    {historyItem.user ? historyItem.user.fullName : 'System'}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

DealHistoryTimeline.propTypes = {
  dealId: PropTypes.number.isRequired,
  currentStage: PropTypes.string.isRequired,
};

export default DealHistoryTimeline;
